<!--
//================================================================================
// ID:-
// ProjectName: 都営住宅募集オンライン申請
// SystemName: 都営住宅募集オンラインシステム
// JavaScriptName:NumberInput
//
// <<Modification History>>
// Version  | Date       | Updated By            | Content
// ---------+------------+-----------------------+--------------------------------
// 01.00.00 | 2021/08/06 | 富士通株式会社         | 新規作成
// 01.01.00 | 2024/03/29 | 富士通Japan株式会社    | 令和5年度改修対応（3次リリース）
//================================================================================
-->
<template>
	<input
		v-bind="$attrs"
		ref="numberInput"
		pattern="\d*"
		:type="inputType"
		:value="innerValue"
		:placeholder="placeholder == null ? '' : placeholder"
		:class="['input-number ime-inactive', {'contents__error--item': errorFlg}, {'no-spin': isEditing}]"
		:disabled="disabledFlg"
		:maxlength="maxLengthAll"
		:tabindex="tabindex"
		@input="$emit('update:modelValue', $event.target.value)"
		@keydown.delete.stop="keyControl"
		@blur="$emit('blur', $event)"
		@keydown="$emit('keydown', $event)"
		@focusin="edit"
		@focusout="display"
	>
</template>

<script>
import { co_checkLength, co_isEmpty } from '@/common/checkUtility.js';
import { co_getMessage } from '@/common/messageUtility.js';

import { NUMBERINPUT_TYPE } from '@/const/const.js';
import { STORE_ERROR_SET_INPUT_ERR, STORE_ERROR_IS_ERRFLG, STORE_ERROR_IS_INPUT_ERRFLG, STORE_VIEW_IS_MOBILE } from '@/const/store.js';
import { MSG_HALF_NUM, MSG_INTEGER, MSG_DECIMAL } from '@/const/messageId.js';

export default {
	inheritAttrs: false,
	props: {
		modelValue: { type: Number, default: null },				// 項目値
		name: { type: String, default: '' },						// 項目名
		requireFlg: { type: Boolean, default: false },				// true : 必須
		numberType: {String, default: NUMBERINPUT_TYPE.INTEGER},	// 書式
		maxLength: { type: Number, default: 0 },					// 整数部の最大文字数
		decimalLength: {type: Number, default: 0},					// 小数部の最大桁数
		placeholder: { type: String, default: null },				// 入力補助
		disabledFlg: { type: Boolean, default: false },				// 活性・非活性フラグ
		tabindex: {type: Number, default: 0},						// タブインデックス
		groupId: {type: String, default: ''},						// 項目グループID
	},
	emits: ['update:modelValue', 'blur', 'keydown'],
	data: function () {
		return {
			errorFlg: false,
			isEditing: false,
			maxLengthAll: 0,
		}
	},
	computed: {
		listeners() {
			const vm = this;
			return {
				...vm.$listenres,
				input: event => vm.innerValue = event
			};
		},
		innerValue: {
			get() {
				if (this.isEditing) {
					return this.modelValue != null ? this.modelValue.toString() : "";
				} else {
					if (this.numberType == NUMBERINPUT_TYPE.PERIOD) {
						// 書式が桁区切りありの場合、カンマ区切り編集
						return this.modelValue != null ? this.commaSeparated(this.modelValue) : "";
					} else {
						return this.modelValue != null ? this.modelValue.toString() : "";
					}
				}
			},
			set(newValue) {
				const value = newValue.length > 0 ? Number(newValue) : null;
				this.$emit('update:modelValue', value);
			}
		},
		inputType: function () {
			return this.isEditing ? "tel" : "text";
			//return "text";
		},
		errFlg: function () {
			return this.$store.getters[STORE_ERROR_IS_ERRFLG];
		}
	},
	watch: {
		disabledFlg: function( val ) {
			if( val ) {
				this.clearError();
			}
		},
		errFlg: function(newValue) {
			if(newValue === false) {
				this.clearError();
			}
		}
	},
	created: function() {
		// maxLength設定
		this.maxLengthAll = this.maxLength;
		if (this.decimalLength > 0) {
			this.maxLengthAll += this.decimalLength + 1;
		}
	},
	methods: {
		keyControl: function () {
			// キーイベントを無効化するためのメソッドのため、処理なし
		},
		// 入力時
		edit: function() {
			this.isEditing = true;
		},
		// ロストフォーカス時
		display: function() {
			this.isEditing = false;
		},
		// 妥当性チェック
		validate: function () {

			let value = this.modelValue == null ? null : String(this.modelValue);
			if ( !co_isEmpty(value) ) {
				value = this.replaceFullToHalf(value);
			}
			this.$emit('update:modelValue', value);

			let error = false;
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];

			// 必須チェック
			if ( this.requireFlg === true ) {
				if ( co_isEmpty(value) ) {
					this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0053', this.name)});
					error = true;
				}
			}

			if ( !co_isEmpty(value) ) {
				const valArr = value.split('.');
				const valArrCnt = valArr.length;

				let errFlg = false;
				let errFlgInt = false;
				let errFlgDecimal = false;

				// 整数値
				if(valArrCnt == 1) {
					// 整数部の桁チェック
					errFlg = !valArr[0].match(/^[0-9]+$/);
				}
				// 小数値
				else if(valArrCnt == 2) {
					// 整数部の桁チェック
					errFlg = !valArr[0].match(/^[0-9]+$/);

					// 小数部の桁チェック
					if (!errFlg) {
						errFlg = !valArr[1].match(/^[0-9]+$/);
					}
				}

				// 数値チェック
				if(errFlg) {
					this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_HALF_NUM)});
					error = true;
				}

				// 入力形式チェック
				if(this.decimalLength == 0) {
					if (valArrCnt > 1) {
						this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0056', this.name, MSG_INTEGER)});
						error = true;
					}
				} else {
					if (valArrCnt > 2) {
						let messageArgs = MSG_DECIMAL.replace(new RegExp('[@]' + 1, 'g'), String(this.decimalLength));
						this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0056', this.name, messageArgs)});
						error = true;
					}
				}

				if (!inputError && error) {
					// エラーの先頭項目の場合、フォーカス設定
					this.focus();
				}

				if (error) {
					this.errorFlg = error;
					return !this.errorFlg;
				}

				// 最大入力桁チェック
				// 整数値
				if(valArrCnt == 1) {
					// 整数部の桁チェック
					errFlgInt = !co_checkLength(valArr[0], this.maxLength);
				}
				// 小数値
				else if(valArrCnt == 2) {
					// 整数部の桁チェック
					errFlgInt = !co_checkLength(valArr[0], this.maxLength);

					// 小数部の桁チェック
					errFlgDecimal = !co_checkLength(valArr[1], this.decimalLength);
				}

				// 桁数チェック
				if (this.decimalLength == 0) {
					if (errFlgInt) {
						this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
									{id: this.groupId, msg: co_getMessage('E0055', this.name, String(this.maxLength))});
						error = true;
					}
				} else {
					if(errFlgInt || errFlgDecimal) {
						let messageArgs = MSG_DECIMAL.replace(new RegExp('[@]' + 1, 'g'), String(this.maxLength))
														.replace(new RegExp('[@]' + 2, 'g'), String(this.decimalLength));
						this.$store.commit(STORE_ERROR_SET_INPUT_ERR,
										{id: this.groupId, msg: co_getMessage('E0056', this.name, messageArgs)});
						error = true;
					}
				}
			}

			if (!inputError && error) {
				// エラーの先頭項目の場合、フォーカス設定
				this.focus();
			}

			this.errorFlg = error;
			return !this.errorFlg;
		},
		// カンマ付与
		commaSeparated : function (num) {
			if ( !String(num).match(/^[0-9]+$/) ) {
				return num;
			}
			return Number(num).toLocaleString();
		},
		// カンマ除去
		removeComma : function (num) {
			var removed = num.replace(/,/g, '');
			return Number(removed);
		},
		// 半角を全角に変換
		replaceFullToHalf : function (str) {
			return str.replace(/[！-～]/g, function(s) {
					return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
			});
		},
		// エラー設定
		setError: function() {
			this.errorFlg = true;
		},
		// エラークリア
		clearError: function() {
			this.errorFlg = false;
		},
		// フォーカス設定
		focus: function() {
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];
			let mobileFlg = this.$store.getters[STORE_VIEW_IS_MOBILE];
			if (inputError || !mobileFlg) {
				this.$refs.numberInput.focus();
			}
		}
	}
}
</script>

<style scoped>
.input-number {
	text-align: right;
}

.no-spin::-webkit-inner-spin-button,
.no-spin::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance:textfield;
}

</style>